import { useEffect, useMemo, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "../components/ui"
import { supabase } from "../supabaseClient"
import { useUser } from "../UserContext"
import useGa4Property from "../hooks/useGa4Property"
import {
  evaluateUserAccessIssues,
  extractTemplateExpectedUsers,
  normalizeUserAccessIdentifier,
} from "../utils/ga4UserAccess"
import "./GA4UserAccessAudit.css"

const cx = (...classes) => classes.filter(Boolean).join(" ")

const trimString = (value) =>
  typeof value === "string" ? value.trim() : ""

const formatDateTime = (value) => {
  if (!value) return null
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return null
  return date.toLocaleString()
}

const STATUS_LABELS = {
  ok: "Confirmed",
  pending: "Needs confirmation",
  new: "New since confirmation",
  missing: "Missing from property",
}

const STATUS_BADGE_CLASSNAMES = {
  ok: "ga4-user-access__status-badge--ok",
  pending: "ga4-user-access__status-badge--pending",
  new: "ga4-user-access__status-badge--new",
  missing: "ga4-user-access__status-badge--missing",
}

const ROW_STATUS_CLASSNAMES = {
  pending: "ga4-user-access__row--pending",
  new: "ga4-user-access__row--new",
  missing: "ga4-user-access__row--missing",
}

const getAuditStatusClassName = (status) => {
  const normalized = typeof status === "string" ? status.toLowerCase() : ""
  if (!normalized || normalized === "ok") return "ga4-user-access__audit-status--ok"
  if (normalized.includes("error") || normalized.includes("fail") || normalized === "missing") {
    return "ga4-user-access__audit-status--error"
  }
  if (normalized === "pending" || normalized === "new") {
    return "ga4-user-access__audit-status--warning"
  }
  if (normalized.includes("warn") || normalized.includes("review")) {
    return "ga4-user-access__audit-status--warning"
  }
  return "ga4-user-access__audit-status--warning"
}

const formatAuditStatusLabel = (status) => {
  if (!status) return "Ok"
  const cleaned = trimString(String(status)).replace(/[_-]+/g, " ")
  if (!cleaned) return "Ok"
  return cleaned.replace(/\b\w/g, (char) => char.toUpperCase())
}

const sanitizeRoleList = (value) =>
  Array.isArray(value)
    ? value
        .map((role) => (typeof role === "string" ? trimString(role) : role))
        .filter(Boolean)
    : []

const ACCESS_LEVEL_LABELS = {
  account: "Account (inherited)",
  property: "Property",
  expected: "Template expectation",
}

export default function GA4UserAccessAudit() {
  const navigate = useNavigate()
  const { search } = useLocation()
  const user = useUser()
  const propertyId = new URLSearchParams(search).get("id")
  const {
    property: propInfo,
    loading: propertyLoading,
    error: propertyError,
  } = useGa4Property(propertyId)

  const [rows, setRows] = useState([])
  const [statusRecord, setStatusRecord] = useState(null)
  const [confirmationRecord, setConfirmationRecord] = useState(null)
  const [expectedUsers, setExpectedUsers] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [reloadToken, setReloadToken] = useState(0)
  const [savingConfirmation, setSavingConfirmation] = useState(false)
  const [saveError, setSaveError] = useState(null)

  useEffect(() => {
    if (propertyLoading) return
    if (propertyError || !propInfo?.id) {
      setRows([])
      setStatusRecord(null)
      setConfirmationRecord(null)
      setExpectedUsers([])
      setError(propertyError || null)
      setLoading(false)
      return
    }

    let cancelled = false
    const load = async () => {
      setLoading(true)
      setError(null)
      try {
        const propertyUuid = propInfo.id
        const templatePromise = propInfo.template_id
          ? supabase
              .from("ga4_templates")
              .select("template")
              .eq("id", propInfo.template_id)
              .maybeSingle()
          : Promise.resolve({ data: null, error: null })
        const [
          rowsResult,
          stateResult,
          confirmationResult,
          templateResult,
        ] = await Promise.all([
          supabase
            .from("ga4_user_access_audit_results")
            .select(
              "id, identifier, normalized_identifier, principal_email, principal_type, user_link_name, direct_roles, effective_roles, access_level",
            )
            .eq("ga4_property_id", propertyUuid)
            .order("principal_email", { ascending: true }),
          supabase
            .from("ga4_user_access_audit_states")
            .select(
              "audit_status, audit_message, audit_reason, last_audited_at, user_count",
            )
            .eq("ga4_property_id", propertyUuid)
            .maybeSingle(),
          supabase
            .from("ga4_user_access_confirmations")
            .select("confirmed_identifiers, confirmed_at, confirmed_by")
            .eq("ga4_property_id", propertyUuid)
            .maybeSingle(),
          templatePromise,
        ])

        if (cancelled) return

        if (rowsResult.error) throw rowsResult.error

        const normalizedRows = (Array.isArray(rowsResult.data)
          ? rowsResult.data
          : []
        )
          .map((row) => {
            const identifier = trimString(row?.identifier) || null
            const principalEmail = trimString(row?.principal_email) || null
            const rawUserLinkName = trimString(row?.user_link_name) || null
            const normalizedIdentifier =
              trimString(row?.normalized_identifier) ||
              normalizeUserAccessIdentifier(identifier) ||
              normalizeUserAccessIdentifier(principalEmail) ||
              normalizeUserAccessIdentifier(rawUserLinkName) ||
              null
            const principalType = trimString(row?.principal_type) || null
            const userLinkName = normalizedIdentifier || rawUserLinkName
            const directRoles = sanitizeRoleList(row?.direct_roles)
            const effectiveRoles = sanitizeRoleList(row?.effective_roles)
            const rawAccessLevel =
              typeof row?.access_level === "string"
                ? trimString(row.access_level)
                : ""
            const accessLevel = rawAccessLevel || null

            const baseId =
              row?.id ||
              normalizedIdentifier ||
              identifier ||
              principalEmail ||
              userLinkName ||
              `row-${Math.random().toString(36).slice(2)}`

            return {
              id: baseId,
              identifier,
              normalizedIdentifier,
              principalEmail,
              principalType,
              userLinkName,
              directRoles,
              effectiveRoles,
              accessLevel,
            }
          })
          .filter((row) => {
            const hasIdentity = Boolean(
              row.principalEmail || row.identifier || row.userLinkName,
            )
            const hasRoles = Boolean(
              (Array.isArray(row.directRoles) && row.directRoles.length > 0) ||
                (Array.isArray(row.effectiveRoles) && row.effectiveRoles.length > 0),
            )
            return hasIdentity || hasRoles
          })

        setRows(normalizedRows)
        setStatusRecord(stateResult.error ? null : stateResult.data)
        setConfirmationRecord(
          confirmationResult.error ? null : confirmationResult.data,
        )
        const templateData = templateResult?.data
        if (templateResult?.error) {
          console.error("[GA4] Failed to load template expectations", templateResult.error)
          setExpectedUsers([])
        } else if (templateData) {
          setExpectedUsers(extractTemplateExpectedUsers(templateData.template))
        } else {
          setExpectedUsers([])
        }
      } catch (err) {
        if (cancelled) return
        console.error("[GA4] Failed to load user access audit", err)
        setRows([])
        setStatusRecord(null)
        setConfirmationRecord(null)
        setExpectedUsers([])
        setError(err)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    load()

    return () => {
      cancelled = true
    }
  }, [propInfo?.id, propInfo?.template_id, propertyLoading, propertyError, reloadToken])

  const auditStatus = statusRecord?.audit_status || "ok"
  const evaluation = useMemo(
    () => {
      const confirmationIdentifiers = Array.isArray(
        confirmationRecord?.confirmed_identifiers,
      )
        ? confirmationRecord.confirmed_identifiers
        : []
      return evaluateUserAccessIssues({
        rows,
        status: auditStatus,
        confirmationIdentifiers,
        expectedUsers,
      })
    },
    [rows, auditStatus, confirmationRecord?.confirmed_identifiers, expectedUsers],
  )

  const displayRows = useMemo(() => {
    const pending = evaluation.confirmationPending
    const newSet = evaluation.newIdentifiers
    const actualRows = rows.map((row) => {
      const normalized = row.normalizedIdentifier
      let status = "ok"
      if (pending) status = "pending"
      else if (normalized && newSet.has(normalized)) status = "new"
      return {
        ...row,
        key: `actual-${row.id}`,
        status,
      }
    })
    const missingRows = evaluation.expectedMissing.map((expected) => ({
      key: `expected-${expected.normalizedIdentifier}`,
      identifier: expected.identifier,
      normalizedIdentifier: expected.normalizedIdentifier,
      userLinkName: expected.label || "",
      principalEmail: expected.identifier,
      directRoles: Array.isArray(expected.requiredRoles)
        ? expected.requiredRoles
        : [],
      effectiveRoles: [],
      notes: expected.notes || "",
      status: "missing",
      accessLevel: "expected",
    }))
    return [...actualRows, ...missingRows].sort((a, b) => {
      const aId = (a.identifier || a.principalEmail || "").toLowerCase()
      const bId = (b.identifier || b.principalEmail || "").toLowerCase()
      if (aId && bId) return aId.localeCompare(bId)
      if (aId) return -1
      if (bId) return 1
      return 0
    })
  }, [rows, evaluation])

  const removedIdentifiers = useMemo(
    () => Array.from(evaluation.removedIdentifiers || new Set()),
    [evaluation.removedIdentifiers],
  )

  const lastAudited = formatDateTime(statusRecord?.last_audited_at)
  const lastConfirmed = formatDateTime(confirmationRecord?.confirmed_at)

  const confirmationAllowedWithoutAudit =
    auditStatus === "insufficient-permission"
  const confirmDisabled =
    (auditStatus !== "ok" && !confirmationAllowedWithoutAudit) ||
    evaluation.expectedMissing.length > 0 ||
    savingConfirmation

  const auditStatusLabel = formatAuditStatusLabel(auditStatus)
  const auditStatusMessage = (() => {
    const normalizedStatus = trimString(auditStatus).toLowerCase()
    const labelLower = auditStatusLabel.toLowerCase()
    const rawMessage = trimString(statusRecord?.audit_message)
    if (rawMessage && rawMessage.toLowerCase() !== labelLower) {
      return rawMessage
    }
    const rawReason = trimString(statusRecord?.audit_reason)
    if (rawReason && rawReason.toLowerCase() !== labelLower) {
      return rawReason
    }
    if (!normalizedStatus || normalizedStatus === "ok") return ""
    if (normalizedStatus === "insufficient-permission") {
      return (
        rawMessage || rawReason || "Does not have access to User Management"
      )
    }
    if (normalizedStatus.includes("error") || normalizedStatus.includes("fail")) {
      return "The GA4 audit ran into an unexpected error. Re-run the audit from the GA4 Audit page once the issue has been resolved."
    }
    if (normalizedStatus === "missing") {
      return "Some required users were missing during the last audit."
    }
    return "Review the audit details and resolve the outstanding items before confirming access."
  })()
  const hasMissing = evaluation.expectedMissing.length > 0

  const handleConfirm = async () => {
    if (!propInfo?.id) return
    setSavingConfirmation(true)
    setSaveError(null)
    try {
      const identifiers = Array.from(evaluation.currentIdentifiers || [])
      const nowIso = new Date().toISOString()
      const payload = {
        ga4_property_id: propInfo.id,
        confirmed_identifiers: identifiers,
        confirmed_at: nowIso,
        confirmed_by: user?.id ?? null,
        updated_at: nowIso,
      }
      const { error: confirmationError } = await supabase
        .from("ga4_user_access_confirmations")
        .upsert(payload)
      if (confirmationError) throw confirmationError
      setReloadToken((token) => token + 1)
    } catch (err) {
      console.error("[GA4] Failed to confirm user access audit", err)
      setSaveError(err)
    } finally {
      setSavingConfirmation(false)
    }
  }

  return (
    <div className="page-container page-container--wide ui-stack ui-stack--xl ga4-user-access">
      <div className="ui-page-header">
        <div className="ui-stack ui-stack--xs">
          <h1 className="ui-page-title">GA4 User Access</h1>
          <p className="ui-subtitle">
            Review GA4 property permissions, reconcile template expectations, and
            document confirmations.
          </p>
        </div>
        <div className="ui-button-row ui-button-row--compact">
          <Button
            variant="secondary"
            onClick={() =>
              navigate(
                propInfo
                  ? `/ga4-property?id=${encodeURIComponent(propInfo.property_id)}`
                  : "/ga4-audit",
              )
            }
          >
            Back
          </Button>
        </div>
      </div>

      {propertyLoading ? (
        <Card>
          <CardContent>
            <p className="ga4-user-access__message">Loading property information…</p>
          </CardContent>
        </Card>
      ) : propertyError ? (
        <Card>
          <CardContent>
            <p className="ga4-user-access__message ga4-user-access__message--error">
              Failed to load property information.
            </p>
          </CardContent>
        </Card>
      ) : !propInfo ? (
        <Card>
          <CardContent>
            <p className="ga4-user-access__message">Property not found.</p>
          </CardContent>
        </Card>
      ) : (
        <>
          <Card>
            <CardHeader className="ui-card__header--split">
              <div className="ui-stack ui-stack--xs">
                <CardTitle>{propInfo.property_name}</CardTitle>
                <CardDescription>Property ID: {propInfo.property_id}</CardDescription>
              </div>
              <Badge
                variant="outline"
                className={cx(
                  "ga4-user-access__audit-status",
                  getAuditStatusClassName(auditStatus),
                )}
              >
                {auditStatusLabel}
              </Badge>
            </CardHeader>
            <CardContent className="ga4-user-access__content">
              <div className="ga4-user-access__summary-grid">
                <div className="ga4-user-access__metric">
                  <span className="ga4-user-access__metric-label">
                    Users requiring review
                  </span>
                  <span className="ga4-user-access__metric-value">
                    {evaluation.issueCount}
                  </span>
                </div>
                <div className="ga4-user-access__metric">
                  <span className="ga4-user-access__metric-label">Users detected</span>
                  <span className="ga4-user-access__metric-value">
                    {statusRecord?.user_count ?? rows.length}
                  </span>
                </div>
              </div>
              <dl className="ga4-user-access__meta">
                <div>
                  <dt>Last audited</dt>
                  <dd>{lastAudited || "—"}</dd>
                </div>
                <div>
                  <dt>Last confirmed</dt>
                  <dd className={cx(!lastConfirmed && "is-warning")}>
                    {lastConfirmed || "Not yet confirmed"}
                  </dd>
                </div>
              </dl>
            </CardContent>
            <CardFooter className="ga4-user-access__footer">
              <div className="ga4-user-access__action-row">
                <Button
                  onClick={handleConfirm}
                  disabled={confirmDisabled || loading}
                >
                  {savingConfirmation
                    ? "Saving…"
                    : "I confirmed I've audited users"}
                </Button>
                {((auditStatus !== "ok" && !confirmationAllowedWithoutAudit) ||
                  hasMissing) && (
                  <span className="ga4-user-access__action-helper">
                    {auditStatus !== "ok"
                      ? "Resolve the audit issue before confirming users."
                      : "Add the missing template users before confirming."}
                  </span>
                )}
              </div>
              {saveError ? (
                <p className="ga4-user-access__message ga4-user-access__message--error">
                  Failed to record confirmation. Please try again.
                </p>
              ) : null}
            </CardFooter>
          </Card>

          {auditStatus !== "ok" ? (
            <div
              className="ga4-user-access__alert ga4-user-access__alert--error"
              role="alert"
            >
              <span className="ga4-user-access__alert-title">Audit status</span>
              <p className="ga4-user-access__alert-message">
                {auditStatusLabel}
                {auditStatusMessage ? `: ${auditStatusMessage}` : ""}
              </p>
            </div>
          ) : null}

          {hasMissing ? (
            <div
              className="ga4-user-access__alert ga4-user-access__alert--warning"
              role="alert"
            >
              <span className="ga4-user-access__alert-title">
                Template expectations missing
              </span>
              <p className="ga4-user-access__alert-message">
                {evaluation.expectedMissing.length} user
                {evaluation.expectedMissing.length === 1 ? "" : "s"} still need to be
                added before confirming.
              </p>
            </div>
          ) : null}

          {removedIdentifiers.length > 0 ? (
            <div className="ga4-user-access__alert ga4-user-access__alert--info">
              <span className="ga4-user-access__alert-title">
                Removed since last confirmation
              </span>
              <ul className="ga4-user-access__alert-list">
                {removedIdentifiers.map((id) => (
                  <li key={id}>{id}</li>
                ))}
              </ul>
            </div>
          ) : null}

          <Card>
            <CardHeader>
              <CardTitle>Current access bindings</CardTitle>
              <CardDescription>
                Reconcile detected GA4 users with template expectations and confirmation
                history.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <p className="ga4-user-access__message">Loading user access…</p>
              ) : error ? (
                <p className="ga4-user-access__message ga4-user-access__message--error">
                  Failed to load user access audit. Please try again.
                </p>
              ) : displayRows.length === 0 ? (
                <p className="ga4-user-access__message">
                  No access bindings were captured during the last audit. Review the GA4
                  property and parent account access bindings to make sure the correct users
                  still have access before confirming this audit.
                </p>
              ) : (
                <TableContainer className="ga4-user-access__table-container">
                  <Table className="ui-table--sticky">
                    <TableHead>
                      <TableRow>
                        <TableHeaderCell>User</TableHeaderCell>
                        <TableHeaderCell>Access Level</TableHeaderCell>
                        <TableHeaderCell>Direct Roles</TableHeaderCell>
                        <TableHeaderCell>Effective Roles</TableHeaderCell>
                        <TableHeaderCell>Status</TableHeaderCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {displayRows.map((row) => {
                        const label = STATUS_LABELS[row.status] || STATUS_LABELS.ok
                        const rowClassName = ROW_STATUS_CLASSNAMES[row.status] || null
                        const statusBadgeClass =
                          STATUS_BADGE_CLASSNAMES[row.status] ||
                          STATUS_BADGE_CLASSNAMES.ok
                        const accessLevelLabel =
                          (row.accessLevel && ACCESS_LEVEL_LABELS[row.accessLevel]) ||
                          (row.status === "missing"
                            ? ACCESS_LEVEL_LABELS.expected
                            : ACCESS_LEVEL_LABELS.property)
                        const showNotes = row.notes && row.notes.trim().length > 0

                        const userLabel =
                          row.principalEmail ||
                          row.userLinkName ||
                          row.identifier ||
                          row.normalizedIdentifier ||
                          "Unknown"

                        return (
                          <TableRow
                            key={row.key}
                            className={cx("ga4-user-access__row", rowClassName)}
                          >
                            <TableCell>
                              <div className="ga4-user-access__user">
                                {userLabel}
                              </div>
                              {(row.userLinkName || row.principalType) && (
                                <div className="ga4-user-access__user-meta">
                                  {row.userLinkName}
                                  {row.userLinkName && row.principalType ? " · " : ""}
                                  {row.principalType}
                                </div>
                              )}
                              {showNotes ? (
                                <div className="ga4-user-access__user-notes">
                                  {row.notes}
                                </div>
                              ) : null}
                            </TableCell>
                            <TableCell>
                              <Badge
                                variant="outline"
                                className="ga4-user-access__access-badge"
                              >
                                {accessLevelLabel}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {row.directRoles && row.directRoles.length
                                ? row.directRoles.join(", ")
                                : "—"}
                            </TableCell>
                            <TableCell>
                              {row.effectiveRoles && row.effectiveRoles.length
                                ? row.effectiveRoles.join(", ")
                                : "—"}
                            </TableCell>
                            <TableCell>
                              <Badge
                                className={cx(
                                  "ga4-user-access__status-badge",
                                  statusBadgeClass,
                                )}
                              >
                                {label}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        )
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  )
}
